/**
 * useApprovalState — 특정 대상(targetId)의 현재 승인 상태 + 이력 조회 (PC·모바일 공용)
 *
 * ApprovalModal Detail/History 뷰, ApprovalFlow 진행 표시기에서 사용.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export const APPROVAL_STATE_KEYS = {
  all: ['approval-kit', 'state'],
  detail: (policyId, targetId) => ['approval-kit', 'state', policyId, String(targetId)],
};

export const useApprovalState = (policyId, targetId, options = {}) =>
  useQuery({
    queryKey: APPROVAL_STATE_KEYS.detail(policyId, targetId),
    queryFn: () =>
      api.get(`/approval-kit/${policyId}/${targetId}/state`).then(r => r.data?.data),
    enabled: !!policyId && !!targetId,
    staleTime: 15 * 1000,
    ...options,
  });

export const useApprovalMutation = (policyId, targetId) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: APPROVAL_STATE_KEYS.detail(policyId, targetId) });

  const request = useMutation({
    mutationFn: (body) =>
      api.post(`/approval-kit/${policyId}/${targetId}/request`, body).then(r => r.data),
    onSuccess: invalidate,
  });
  const approve = useMutation({
    mutationFn: (body) =>
      api.post(`/approval-kit/${policyId}/${targetId}/approve`, body).then(r => r.data),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (body) =>
      api.post(`/approval-kit/${policyId}/${targetId}/reject`, body).then(r => r.data),
    onSuccess: invalidate,
  });

  return { request, approve, reject };
};

export const useApprovalInbox = (params) =>
  useQuery({
    queryKey: ['approval-kit', 'inbox', params],
    queryFn: () => api.get('/approval-kit/inbox', { params }).then(r => r.data?.data),
    staleTime: 30 * 1000,
  });

/**
 * useApprovalInboxSummary — 알림센터 배지 카운트·정책별 집계·내 최근 결과
 *
 * NotificationCenter/MobileNotificationCenter가 이 훅을 사용하여
 * '승인 대기 N건' 배지와 빠른 링크를 표시한다.
 */
export const useApprovalInboxSummary = () =>
  useQuery({
    queryKey: ['approval-kit', 'inbox', 'summary'],
    queryFn: () => api.get('/approval-kit/inbox/summary').then(r => r.data?.data),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
